import * as React from 'react'
import * as UI from '@brewlogix/blx-ui'

import { CompositeProduct } from 'src/types/schema/graphql'
import { useStickyNav } from 'src/features/locations/state/useStickyNav'
import { useRouter } from 'next/router'
import { parseEnumLabel } from 'src/util/helpers'
import BrewCard from './BrewCard'
import { useBrewsListStore } from '../state/useBrewsListStore'
import Link from 'next/link'

interface IBrewsList {
    allProducts: CompositeProduct[]
    brewsRef?: React.RefObject<HTMLDivElement>
}
type InputOptionType = {
    label: string
    value: string
}

const BrewsList: React.FC<IBrewsList> = ({ allProducts, brewsRef }) => {
    const { space } = useStickyNav()
    const [inventorySearchTerm, setInventorySearchTerm] = React.useState('')
    const [availabilityOptions, setAvailabilityOptions] = React.useState([])
    const [styleOptions, setStyleOptions] = React.useState([])

    const page = useBrewsListStore((state) => state.page)
    const setPage = useBrewsListStore((state) => state.setPage)
    const pageMax = useBrewsListStore((state) => state.pageMax)
    const isPrevDisabled = useBrewsListStore((state) => state.isPrevDisabled)
    const setIsPrevDisabled = useBrewsListStore((state) => state.setIsPrevDisabled)
    const isNextDisabled = useBrewsListStore((state) => state.isNextDisabled)
    const setIsNextDisabled = useBrewsListStore((state) => state.setIsNextDisabled)
    const setPageMax = useBrewsListStore((state) => state.setPageMax)
    const setFilteredProducts = useBrewsListStore((state) => state.setFilteredProducts)
    const createPaginatedArray = useBrewsListStore((state) => state.createPaginatedArray)
    const paginatedProducts = useBrewsListStore((state) => state.paginatedProducts)
    const styleFilter = useBrewsListStore((state) => state.styleFilter)
    const setStyleFilter = useBrewsListStore((state) => state.setStyleFilter)
    const searchTerm = useBrewsListStore((state) => state.searchTerm)
    const setSearchTerm = useBrewsListStore((state) => state.setSearchTerm)
    const availabilityFilter = useBrewsListStore((state) => state.availabilityFilter)
    const setAvailabilityFilter = useBrewsListStore((state) => state.setAvailabilityFilter)
    const resetBrewListStore = useBrewsListStore((state) => state.resetBrewListStore)

    const router = useRouter()

    React.useEffect(() => {
        setSearchTerm(inventorySearchTerm)
        inventorySearch(inventorySearchTerm, styleFilter, availabilityFilter)
    }, [inventorySearchTerm, availabilityFilter])

    type StyleItem = { label: string; value: string }

    React.useEffect(() => {
        const availability = {}
        const styles: StyleItem[] = []
        allProducts.forEach((item, index) => {
            if (item.traits && item.traits.availability) {
                availability[item.traits.availability] = {
                    label: parseEnumLabel(item.traits.availability),
                    value: item.traits.availability,
                }
            }
            if (item.style && item.style.title) {
                if (!styles.find(({ label }) => label === item.style.title)) {
                    styles.push({
                        label: item.style.title,
                        value: item.style.title,
                    })
                }
            }
        })
        setAvailabilityOptions(Object.values(availability))
        const sortedStyles = styles.sort((a, b) => a.label.localeCompare(b.label))
        setStyleOptions(sortedStyles)
        return () => resetBrewListStore()
    }, [allProducts])

    const getCiderAndKombuchaType = (item: CompositeProduct) => {
        if (item?.__typename === 'Kombucha')
            return item?.kombuchaFamily ? String(item?.kombuchaFamily) : 'Kombucha'
        else if (item?.__typename === 'Cider')
            return item?.ciderFamily ? String(item?.ciderFamily) : 'Cider'
        return ''
    }

    const inventorySearch = (
        searchTerm: string,
        styleFilter: string,
        availabilityFilter: string
    ) => {
        const filtered = allProducts.filter((item) => {
            const title = item?.title?.toLowerCase()
            const hasStyle = item?.style
            const style = hasStyle ? hasStyle?.title?.toLowerCase() : getCiderAndKombuchaType(item)
            const availability = String(item?.traits?.availability)?.toLowerCase()

            return (
                !item.isHiddenExternally &&
                (title?.includes(searchTerm.toLowerCase()) ||
                    style?.includes(searchTerm.toLowerCase())) &&
                style?.includes(styleFilter.toLowerCase()) &&
                availability?.includes(availabilityFilter.toLowerCase())
            )
        })
        setFilteredProducts(filtered, 10)
        setPage(1)
        const paginatedArr = createPaginatedArray(filtered, 10)
        setPageMax(paginatedArr?.length)
    }

    const handleButtonsDisable = (page: number) => {
        page > 1 ? setIsPrevDisabled(false) : setIsPrevDisabled(true)
        page < pageMax ? setIsNextDisabled(false) : setIsNextDisabled(true)
    }

    const handlePrevButtonClick = () => {
        brewsRef.current?.scrollIntoView({ behavior: 'smooth' })
        setPage(page - 1)
        setTimeout(() => {
            const currPage = page - 1
            handleButtonsDisable(currPage)
        }, 1000)
    }

    const handleNextButtonClick = () => {
        brewsRef.current?.scrollIntoView({ behavior: 'smooth' })
        setPage(page + 1)
        setTimeout(() => {
            const currPage = page + 1
            handleButtonsDisable(currPage)
        }, 1000)
    }

    const handleAvailabilityChange = (e: InputOptionType) => {
        if (e?.value) {
            setAvailabilityFilter(e.value)
            inventorySearch(searchTerm, styleFilter, e.value)
        } else {
            setAvailabilityFilter('')
            inventorySearch(searchTerm, styleFilter, '')
        }
    }
    const handleStyleFilterChange = (e: InputOptionType) => {
        if (e?.value) {
            setStyleFilter(e.value.toLowerCase())
            inventorySearch(searchTerm, e.value, availabilityFilter)
        } else {
            setStyleFilter('')
            inventorySearch(searchTerm, '', availabilityFilter)
        }
    }
    const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value)
        setInventorySearchTerm(e.target.value)
    }
    const availabilityValue = availabilityOptions.find((item) => item.value === availabilityFilter)
    return (
        <div className={space} id='brews' ref={brewsRef}>
            <UI.Heading text='Brews' size='h4' type='h2' customClasses='text-xl lg:text-2xl' />
            <div className='flex w-full flex-col items-baseline justify-between space-y-4 lg:flex-row lg:space-x-6'>
                <UI.InputGroup
                    type='search'
                    icon={<UI.Icon slug='HeroIcons.Search' size='sm' />}
                    customClasses=' w-full'
                    id='searchInventory'
                    onChange={(e) => handleSearchTermChange(e)}
                    value={inventorySearchTerm}
                    label='Search Inventory'
                />

                <div className=' flex w-full flex-col space-y-4 md:flex-row md:space-x-6 md:space-y-0'>
                    <UI.DynamicSelect
                        id='filter-style'
                        placeholder='Style'
                        label=''
                        size='sm'
                        aria-label='Filter by Style'
                        customWrapperClasses=' w-full  md:min-w-24  text-sm '
                        isSearchable={true}
                        isClearable={true}
                        options={styleOptions}
                        onChange={(e) => handleStyleFilterChange(e)}
                    />

                    <UI.DynamicSelect
                        id='filter-availability'
                        placeholder='Availability'
                        label=''
                        aria-label='Filter by Availability'
                        size='sm'
                        customWrapperClasses='w-full md:min-w-24 text-sm'
                        isSearchable={true}
                        isClearable={true}
                        options={availabilityOptions}
                        onChange={(e) => handleAvailabilityChange(e)}
                        value={availabilityValue}
                    />
                </div>
            </div>
            <div className='py-6'>
                {paginatedProducts?.length > 0 && (
                    <div className='space-y-2'>
                        {paginatedProducts[page - 1].map((brew, i) => (
                            <div key={brew.id}>
                                <Link href={`/brewer/${brew?.brand?.slug}/products/${brew?.slug}`}>
                                    <a aria-label={`Product page for ${brew?.title}`}>
                                        <BrewCard
                                            brew={brew}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    </a>
                                </Link>
                            </div>
                        ))}
                        {paginatedProducts[page - 1].length <= 0 && (
                            <UI.Card
                                format='inline'
                                customClasses={`brew-card bg-white`}
                                image={
                                    <UI.Icon
                                        slug='HeroIcons.ExclamationCircle'
                                        size='sm'
                                        theme='dark'
                                    />
                                }
                            >
                                <UI.Heading text='No results' size='h5' />
                            </UI.Card>
                        )}
                    </div>
                )}
            </div>
            <div className='flex w-full justify-between'>
                <p>
                    Page {page} of {pageMax}
                </p>
                <div className='flex'>
                    <UI.Button
                        label='Previous'
                        iconAlignment='left'
                        disabled={isPrevDisabled}
                        onClick={handlePrevButtonClick}
                        customClasses='mr-2'
                        size='sm'
                    />
                    <UI.Button
                        label='Next'
                        iconAlignment='right'
                        disabled={isNextDisabled}
                        onClick={handleNextButtonClick}
                        size='sm'
                    />
                </div>
            </div>
        </div>
    )
}

export default BrewsList
